import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Button } from 'antd';

import { stringSorter } from 'utils';
import { CurrencyDisplay, ProTable, DateDisplay } from 'components/layout';
import {
  renderProTableSearchProjectCategory,
  renderProTableSearchFaculty,
  renderProTableSearchDepartment,
} from 'components/layout/ProTable';
import { GET_MY_CURRENT_USER_ROLE } from 'gql/auth';
import { GET_PROJECTS_FOR_ANNUAL_REPORT } from 'gql/projects';
import { useHasScopeType } from 'hooks';
import { PageContainer } from '@ant-design/pro-layout';
import { SCOPE_TYPES } from 'constants/scopes';

const Projects = () => {
  const { data: roleData, loading: loadingMe } = useQuery(
    GET_MY_CURRENT_USER_ROLE
  );
  const { hasScopeType, loading: loadingScope } = useHasScopeType(
    SCOPE_TYPES.VIEW_ALL_ANNUAL_REPORT
  );
  const [inProgressProjects, setInProgressProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const formRef = useRef();
  const { refetch } = useQuery(GET_PROJECTS_FOR_ANNUAL_REPORT, {
    variables: {
      departments: hasScopeType
        ? []
        : roleData?.myCurrentUserRole?.department?.id
        ? [roleData?.myCurrentUserRole?.department?.id]
        : [],
      ignoreNotAuthorised: true,
    },
  });

  const columns = [
    {
      title: 'Project Category',
      key: 'projectCategory',
      width: 152,
      sorter: (a, b) =>
        stringSorter(a.projectCategory.name, b.projectCategory.name),
      render: ({ projectCategory, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectCategory.name}</Link>
      ),
      renderFormItem: renderProTableSearchProjectCategory,
    },
    {
      title: 'Faculty',
      key: 'faculty',
      hideInTable: true,
      renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
        renderProTableSearchFaculty(
          _,
          { type, defaultValue, disabled: !hasScopeType, ...rest },
          form
        ),
      initialValue: hasScopeType
        ? undefined
        : roleData?.myCurrentUserRole?.faculty?.id,
    },
    {
      title: 'Department',
      key: 'department',
      sorter: (a, b) =>
        stringSorter(
          a.profile?.department?.abbreviation,
          b.profile?.department?.abbreviation
        ),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile?.department?.abbreviation ?? '-'}
        </Link>
      ),
      renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
        renderProTableSearchDepartment(
          _,
          {
            type,
            defaultValue,
            disabled:
              !hasScopeType &&
              (roleData?.myCurrentUserRole?.department?.controlType ===
                'DEPARTMENT' ||
                roleData?.myCurrentUserRole?.department?.controlType === 'NAU'),
            ...rest,
          },
          form
        ),
      initialValue:
        !hasScopeType &&
        (roleData?.myCurrentUserRole?.department?.controlType ===
          'DEPARTMENT' ||
          roleData?.myCurrentUserRole?.department?.controlType === 'NAU')
          ? roleData?.myCurrentUserRole?.department?.id
          : undefined,
    },
    {
      title: 'Project Title',
      dataIndex: 'title',
      width: 232,
      ellipsis: true,
      sorter: (a, b) => stringSorter(a.title, b.title),
      render: (_, { id }) => <Link to={`/projects/${id}/profile`}>{_}</Link>,
      search: false,
    },
    {
      title: 'Work Prog.',
      key: 'workProgramme',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(a.profile?.workProgramme, b.profile?.workProgramme),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile?.workProgramme ?? '-'}
        </Link>
      ),
    },
    {
      title: 'Project Status',
      key: 'projectStatus',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(a.projectStatus?.name, b.projectStatus?.name),
      render: ({ projectStatus, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectStatus?.name ?? '-'}</Link>
      ),
    },
    {
      title: 'Scheduled Start Date',
      key: 'scheduledStartDate',
      hideInSearch: true,
      sorter: (a, b) =>
        a.profile?.originalStartDate - b.profile?.originalStartDate,
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <DateDisplay date={profile?.originalStartDate} />
        </Link>
      ),
    },
    {
      title: 'Actual Completion Date',
      key: 'actualCompletionDate',
      hideInSearch: true,
      sorter: (a, b) =>
        a.profile?.actualCompletionDate - b.profile?.actualCompletionDate,
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <DateDisplay date={profile?.actualCompletionDate} />
        </Link>
      ),
    },
    {
      title: 'Extension Obtained',
      key: 'extensionObtained',
      hideInSearch: true,
      sorter: (a, b) => a.extensionObtained - b.extensionObtained,
      render: ({ extensionObtained, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {extensionObtained > 0 ? extensionObtained : '-'}
        </Link>
      ),
    },
    {
      title: 'Funding Received',
      key: 'fundingReceived',
      hideInSearch: true,
      sorter: (a, b) => a.profile.totalFunding - b.profile.totalFunding,
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <CurrencyDisplay amount={profile?.totalFunding} />
        </Link>
      ),
    },
    {
      title: 'Completion Report',
      key: 'completionReport',
      hideInSearch: true,
      sorter: (a, b) => a.completionReport?.id - b.completionReport?.id,
      render: ({ completionReport, id }) =>
        completionReport ? (
          <Link
            to={`/projects/${id}/report-submissions/${completionReport.id}`}
          >
            <Button type="primary">View Report</Button>
          </Link>
        ) : (
          <Link to={`/projects/${id}/profile`}>-</Link>
        ),
    },
    {
      title: 'Submission Date',
      key: 'submissionDate',
      hideInSearch: true,
      sorter: (a, b) =>
        a.completionReport?.actualSubmissionDate -
        b.completionReport?.actualSubmissionDate,
      render: ({ completionReport, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <DateDisplay date={completionReport?.actualSubmissionDate} />
        </Link>
      ),
    },
  ];
  const columnsInProgress = [
    {
      title: 'Project Category',
      key: 'projectCategory',
      width: 152,
      sorter: (a, b) =>
        stringSorter(a.projectCategory.name, b.projectCategory.name),
      render: ({ projectCategory, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectCategory.name}</Link>
      ),
      renderFormItem: renderProTableSearchProjectCategory,
    },
    {
      title: 'Department',
      key: 'department',
      sorter: (a, b) =>
        stringSorter(
          a.profile?.department?.abbreviation,
          b.profile?.department?.abbreviation
        ),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile?.department?.abbreviation ?? '-'}
        </Link>
      ),
      renderFormItem: renderProTableSearchDepartment,
      // initialValue: query.department,
    },
    {
      title: 'Project Title',
      dataIndex: 'title',
      width: 232,
      ellipsis: true,
      sorter: (a, b) => stringSorter(a.title, b.title),
      render: (_, { id }) => <Link to={`/projects/${id}/profile`}>{_}</Link>,
    },
    {
      title: 'Work Prog.',
      key: 'workProgramme',
      sorter: (a, b) =>
        stringSorter(a.profile?.workProgramme, b.profile?.workProgramme),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile?.workProgramme ?? '-'}
        </Link>
      ),
    },
    {
      title: 'Project Status',
      key: 'projectStatus',
      sorter: (a, b) =>
        stringSorter(a.projectStatus?.name, b.projectStatus?.name),
      render: ({ projectStatus, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectStatus?.name ?? '-'}</Link>
      ),
    },
    {
      title: 'Extension Obtained',
      key: 'extensionObtained',
      sorter: (a, b) => a.extensionObtained - b.extensionObtained,
      render: ({ extensionObtained, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {extensionObtained > 0 ? extensionObtained : '-'}
        </Link>
      ),
    },
    {
      title: 'Scheduled Completion Date',
      key: 'scheduledCompletionDate',
      sorter: (a, b) => a.extensionObtained - b.extensionObtained,
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <DateDisplay
            date={
              profile?.lastApprovedCompletionDate ??
              profile?.originalCompletionDate
            }
          />
        </Link>
      ),
    },
    {
      title: 'Latest Progress Report',
      key: 'latestProgressReport',
      sorter: (a, b) => a.latestProgressReport?.id - b.latestProgressReport?.id,
      render: ({ latestProgressReport, id }) =>
        latestProgressReport ? (
          <Link
            to={`/projects/${id}/report-submissions/${latestProgressReport.id}`}
          >
            <Button type="primary">View Report</Button>
          </Link>
        ) : (
          <Link to={`/projects/${id}/profile`}>-</Link>
        ),
    },
    {
      title: 'Submission Date',
      key: 'submissionDate',
      hideInSearch: true,
      sorter: (a, b) =>
        a.latestProgressReport?.actualSubmissionDate -
        b.latestProgressReport?.actualSubmissionDate,
      render: ({ latestProgressReport, id }) => (
        <Link to={`/projects/${id}/profile`}>
          <DateDisplay date={latestProgressReport?.actualSubmissionDate} />
        </Link>
      ),
    },
  ];

  return (
    <PageContainer loading={loadingMe || loadingScope}>
      <div className="annual-report-projects-table-container">
        <ProTable
          scroll={{ x: 1080 }}
          size="small"
          headerTitle="Projects completed, terminated or withdrawn"
          columns={columns}
          request={async (params = {}, _sort, _filter) => {
            try {
              setLoading(true);
              const { department, faculty, projectCategory } = params;
              const result = await refetch({
                departments: department ? [department] : null,
                faculties: faculty ? [faculty] : null,
                projectCategories: projectCategory ? [projectCategory] : null,
              });
              setInProgressProjects(
                result.data?.projectsForAnnualReport.inProgress
              );
              setLoading(false);
              return {
                data: result.data?.projectsForAnnualReport.completed,
                success: true,
              };
            } catch (error) {
              console.log(error);
              setLoading(false);
              return {
                data: [],
                success: false,
              };
            }
          }}
          formRef={formRef}
          options={{
            density: false,
          }}
          search={{
            defaultCollapsed: false,
            searchText: 'Search',
            span: {
              xs: 24,
              sm: 24,
              md: 12,
              lg: 12,
              xl: 8,
              xxl: 8,
            },
            labelWidth: 'auto',
          }}
          pagination={{ pageSize: 20 }}
          rowKey="id"
          onReset={() => {
            if (formRef.current) {
              formRef.current.setFieldsValue({
                faculty: null,
                department: null,
                projectCategories: null,
              });
              formRef.current.submit();
            }
          }}
        />
        <ProTable
          scroll={{ x: 1080 }}
          style={{ marginTop: '1rem' }}
          headerTitle="Projects in Progress"
          columns={columnsInProgress}
          options={{
            density: false,
          }}
          loading={loading}
          search={false}
          pagination={{ pageSize: 20 }}
          size="small"
          rowKey="id"
          dataSource={inProgressProjects}
        />
      </div>
    </PageContainer>
  );
};

export default Projects;
